import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, BACKEND_URL } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Play, Pause, RotateCcw, X, Check, Trophy, Volume2, VolumeX, UserPlus, Timer, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';

/* ---------- Audio (Web Audio API, sin assets) ---------- */
function useBeeper() {
  const ctxRef = useRef(null);
  const enabledRef = useRef(true);

  const ensureCtx = () => {
    if (!enabledRef.current) return null;
    if (!ctxRef.current) {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        ctxRef.current = new AC();
      } catch { return null; }
    }
    if (ctxRef.current.state === 'suspended') {
      try { ctxRef.current.resume(); } catch { /* ignore */ }
    }
    return ctxRef.current;
  };

  const beep = useCallback((freq = 880, durMs = 150, vol = 0.4) => {
    const ctx = ensureCtx();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = freq;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(vol, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durMs / 1000);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + durMs / 1000 + 0.05);
  }, []);

  return {
    beep,
    setEnabled: (v) => { enabledRef.current = v; },
    isEnabled: () => enabledRef.current,
    ensureCtx,
  };
}

/* ---------- Formatter ---------- */
const fmt = (ms) => {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};
const fmtFull = (ms) => {
  // Force MM:SS or HH:MM:SS depending on duration
  return fmt(ms);
};

/* ---------- Page ---------- */
export default function EntrenoTimer() {
  const navigate = useNavigate();
  const { beep, setEnabled, ensureCtx } = useBeeper();
  const [soundOn, setSoundOn] = useState(true);

  // Catálogo
  const [entrenos, setEntrenos] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedEntrenoId, setSelectedEntrenoId] = useState('');

  // Clientes añadidos manualmente: [{id, nombre, foto_perfil, tiempo_ms?, saved?}]
  const [clientes, setClientes] = useState([]);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  // Cronómetro
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0); // ms
  const baseRef = useRef(0);
  const startedAtRef = useRef(0);

  // Finalización
  const [finishing, setFinishing] = useState(false);

  /* ---- Cargar datos ---- */
  useEffect(() => {
    (async () => {
      try {
        const [eRes, uRes] = await Promise.all([
          axios.get(`${API}/hall-of-fame/entrenos`),
          axios.get(`${API}/users`),
        ]);
        setEntrenos(eRes.data || []);
        // Filtrar entrenador y solo aprobados
        const lista = (uRes.data || []).filter(u => u.aprobado && u.role !== 'entrenador');
        // Ordenar alfabético
        lista.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
        setUsers(lista);
      } catch (e) {
        toast.error('Error cargando datos');
      }
    })();
  }, []);

  /* ---- Tick del cronómetro ---- */
  useEffect(() => {
    if (!running) return undefined;
    let raf;
    const tick = () => {
      setElapsed(baseRef.current + (performance.now() - startedAtRef.current));
      raf = requestAnimationFrame(tick);
    };
    startedAtRef.current = performance.now();
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [running]);

  /* ---- Wake lock para tablet ---- */
  useEffect(() => {
    const lockRef = { current: null };
    const request = async () => {
      try {
        if ('wakeLock' in navigator) {
          lockRef.current = await navigator.wakeLock.request('screen');
        }
      } catch { /* ignore */ }
    };
    request();
    const onVis = () => { if (document.visibilityState === 'visible') request(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      if (lockRef.current) try { lockRef.current.release(); } catch { /* ignore */ }
    };
  }, []);

  useEffect(() => { setEnabled(soundOn); }, [soundOn, setEnabled]);

  /* ---- Helpers ---- */
  const selectedEntreno = useMemo(
    () => entrenos.find(e => e.id === selectedEntrenoId),
    [entrenos, selectedEntrenoId]
  );

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.toLowerCase();
    const selectedIds = new Set(clientes.map(c => c.id));
    return users
      .filter(u => !selectedIds.has(u.id) && (u.nombre || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [users, search, clientes]);

  const algunoConTiempo = clientes.some(c => c.tiempo_ms != null);

  const addCliente = (u) => {
    setClientes(prev => [...prev, { id: u.id, nombre: u.nombre, foto_perfil: u.foto_perfil || null }]);
    setSearch('');
    setSearchOpen(false);
  };

  const removeCliente = (id) => {
    setClientes(prev => prev.filter(c => c.id !== id));
  };

  /* ---- Cronómetro controles ---- */
  const start = () => {
    if (!selectedEntrenoId) { toast.error('Elige un entreno primero'); return; }
    if (clientes.length === 0) { toast.error('Añade al menos un cliente'); return; }
    if (running) return;
    baseRef.current = elapsed;
    setRunning(true);
    beep(880, 200);
  };
  const pause = () => { setRunning(false); beep(440, 120); };
  const reset = () => {
    if (running) return;
    if (!window.confirm('¿Resetear cronómetro a 0? Se borrarán los tiempos no guardados.')) return;
    setElapsed(0);
    setClientes(prev => prev.map(c => ({ id: c.id, nombre: c.nombre, foto_perfil: c.foto_perfil })));
    baseRef.current = 0;
  };

  /* ---- Click en cliente: registrar tiempo ---- */
  const registrarTiempo = (id) => {
    setClientes(prev => prev.map(c => {
      if (c.id !== id) return c;
      if (c.tiempo_ms != null) {
        if (!window.confirm(`${c.nombre} ya tiene ${fmtFull(c.tiempo_ms)}. ¿Sobrescribir?`)) return c;
      }
      beep(1000, 150);
      return { ...c, tiempo_ms: elapsed };
    }));
  };

  /* ---- Finalizar y subir al Hall of Fame ---- */
  const finalizar = async () => {
    const conTiempo = clientes.filter(c => c.tiempo_ms != null);
    if (conTiempo.length === 0) {
      toast.error('Ningún cliente tiene tiempo registrado');
      return;
    }
    if (!window.confirm(`Subir ${conTiempo.length} tiempo(s) al Hall of Fame y reiniciar?`)) return;
    setRunning(false);
    setFinishing(true);
    try {
      const fecha = new Date().toISOString().slice(0, 10);
      const results = await Promise.allSettled(
        conTiempo.map(c =>
          axios.post(`${API}/hall-of-fame`, {
            user_id: c.id,
            entreno_id: selectedEntrenoId,
            tiempo: fmtFull(c.tiempo_ms),
            fecha,
            notas: 'Cronómetro directo',
          })
        )
      );
      const ok = results.filter(r => r.status === 'fulfilled').length;
      const ko = results.length - ok;
      if (ok > 0) toast.success(`🏆 ${ok} tiempo${ok > 1 ? 's' : ''} subido${ok > 1 ? 's' : ''} al Hall of Fame`);
      if (ko > 0) toast.error(`${ko} fallaron al subir`);
      // Reset interfaz
      setClientes([]);
      setElapsed(0);
      baseRef.current = 0;
      setSelectedEntrenoId('');
    } catch (e) {
      toast.error('Error al subir al Hall of Fame');
    } finally {
      setFinishing(false);
    }
  };

  /* ---- Render ---- */
  const handleToggleSound = () => {
    setSoundOn(s => {
      const next = !s;
      if (next) { ensureCtx(); beep(660, 80); }
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="entreno-timer-page">
      <Navbar />
      <div className="max-w-5xl mx-auto p-3 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-300 hover:text-gold flex items-center gap-1 px-3 py-2 rounded-lg hover:bg-gray-900 transition-colors"
              data-testid="btn-back"
            >
              <ArrowLeft className="h-5 w-5" />
              <span className="hidden sm:inline">Volver</span>
            </button>
            <h1 className="text-2xl sm:text-3xl font-black text-gold flex items-center gap-2">
              <Timer className="h-7 w-7" /> Cronómetro Entreno
            </h1>
          </div>
          <button
            onClick={handleToggleSound}
            className={`px-3 py-2 rounded-lg flex items-center gap-2 text-xs ${soundOn ? 'bg-emerald-900/40 border border-emerald-700 text-emerald-300' : 'bg-gray-900 border border-gray-700 text-gray-400'}`}
            data-testid="btn-toggle-sound"
          >
            {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            {soundOn ? 'Sonido ON' : 'Sonido OFF'}
          </button>
        </div>

        {/* Setup: entreno + clientes */}
        <Card className="bg-gray-950/60 border-gray-800 p-4 sm:p-6 mb-5">
          {/* Selector de entreno */}
          <label className="block mb-4">
            <div className="text-xs text-gray-400 uppercase tracking-widest mb-1.5">Entreno</div>
            <select
              value={selectedEntrenoId}
              onChange={(e) => setSelectedEntrenoId(e.target.value)}
              disabled={running}
              className="w-full bg-gray-900 border border-gold/40 rounded-xl px-4 py-3 text-white text-lg focus:outline-none focus:border-gold disabled:opacity-50"
              data-testid="select-entreno"
            >
              <option value="">— Elige un entreno del Hall of Fame —</option>
              {entrenos.map(e => (
                <option key={e.id} value={e.id}>{e.nombre}</option>
              ))}
            </select>
            {selectedEntreno?.descripcion && (
              <div className="text-xs text-gray-500 mt-1.5 italic">{selectedEntreno.descripcion}</div>
            )}
          </label>

          {/* Añadir cliente */}
          <div className="relative">
            <div className="text-xs text-gray-400 uppercase tracking-widest mb-1.5">Añadir cliente</div>
            <div className="relative">
              <UserPlus className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setSearchOpen(true); }}
                onFocus={() => setSearchOpen(true)}
                placeholder="Empieza a escribir el nombre…"
                className="w-full pl-9 pr-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white text-base focus:outline-none focus:border-gold/60"
                data-testid="input-search-cliente"
              />
            </div>
            {searchOpen && filteredUsers.length > 0 && (
              <div className="absolute z-30 mt-1 w-full bg-gray-950 border border-gray-700 rounded-xl shadow-2xl max-h-64 overflow-y-auto" data-testid="search-results">
                {filteredUsers.map(u => (
                  <button
                    key={u.id}
                    onClick={() => addCliente(u)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-900 text-left border-b border-gray-900 last:border-b-0"
                    data-testid={`search-result-${u.id}`}
                  >
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-800 flex items-center justify-center text-xs font-bold text-gray-300">
                      {u.foto_perfil ? (
                        <img src={u.foto_perfil.startsWith('http') ? u.foto_perfil : `${BACKEND_URL}${u.foto_perfil}`} alt="" className="w-full h-full object-cover" />
                      ) : (u.nombre || 'U')[0].toUpperCase()}
                    </div>
                    <span className="text-white text-sm">{u.nombre}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Chips de clientes */}
          {clientes.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="chips-clientes">
              {clientes.map(c => (
                <div key={c.id} className="flex items-center gap-2 bg-gray-900 border border-gray-700 rounded-full pl-1 pr-3 py-1">
                  <div className="w-6 h-6 rounded-full overflow-hidden bg-gray-800 flex items-center justify-center text-[10px] font-bold text-gray-300">
                    {c.foto_perfil ? (
                      <img src={c.foto_perfil.startsWith('http') ? c.foto_perfil : `${BACKEND_URL}${c.foto_perfil}`} alt="" className="w-full h-full object-cover" />
                    ) : (c.nombre || 'U')[0].toUpperCase()}
                  </div>
                  <span className="text-sm text-white">{c.nombre}</span>
                  {!running && (
                    <button onClick={() => removeCliente(c.id)} className="text-gray-500 hover:text-red-400" data-testid={`remove-${c.id}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Display del cronómetro */}
        <Card className="bg-gradient-to-b from-gray-950 to-black border-gold/30 p-6 mb-5">
          <div className="flex flex-col items-center">
            <div
              className={`text-[20vw] sm:text-[14vw] md:text-[12vw] lg:text-[9rem] font-black tabular-nums leading-none tracking-tighter ${running ? 'text-emerald-400' : 'text-gold'}`}
              data-testid="timer-display"
            >
              {fmtFull(elapsed)}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
              {!running ? (
                <Button
                  onClick={start}
                  disabled={!selectedEntrenoId || clientes.length === 0}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xl px-8 py-7 rounded-2xl disabled:opacity-40"
                  data-testid="btn-start"
                >
                  <Play className="h-6 w-6 mr-2" /> {elapsed > 0 ? 'Reanudar' : 'Iniciar'}
                </Button>
              ) : (
                <Button onClick={pause} className="bg-amber-600 hover:bg-amber-700 text-white text-xl px-8 py-7 rounded-2xl" data-testid="btn-pause">
                  <Pause className="h-6 w-6 mr-2" /> Pausar
                </Button>
              )}
              <Button
                onClick={reset}
                disabled={running || elapsed === 0}
                variant="outline"
                className="border-gray-600 text-gray-300 text-lg px-6 py-7 rounded-2xl disabled:opacity-40"
                data-testid="btn-reset"
              >
                <RotateCcw className="h-5 w-5 mr-2" /> Reset
              </Button>
            </div>
          </div>
        </Card>

        {/* Tarjetas de clientes para registrar */}
        {clientes.length > 0 && (
          <Card className="bg-gray-950/60 border-gray-800 p-4 sm:p-5 mb-5">
            <div className="text-xs text-gray-400 uppercase tracking-widest mb-3">
              {running ? '👇 Toca al cliente cuando termine' : 'Clientes'}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="clientes-grid">
              {clientes.map(c => {
                const done = c.tiempo_ms != null;
                return (
                  <button
                    key={c.id}
                    onClick={() => running ? registrarTiempo(c.id) : null}
                    disabled={!running}
                    className={`relative flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all min-h-[120px] ${done ? 'bg-emerald-950/40 border-emerald-600' : running ? 'bg-gray-900 border-gray-700 hover:border-gold active:scale-95 cursor-pointer' : 'bg-gray-900/50 border-gray-800'}`}
                    data-testid={`cliente-card-${c.id}`}
                  >
                    {done && (
                      <div className="absolute top-2 right-2 bg-emerald-600 rounded-full p-1">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                    )}
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-800 flex items-center justify-center text-base font-bold text-gray-300 mb-2">
                      {c.foto_perfil ? (
                        <img src={c.foto_perfil.startsWith('http') ? c.foto_perfil : `${BACKEND_URL}${c.foto_perfil}`} alt="" className="w-full h-full object-cover" />
                      ) : (c.nombre || 'U')[0].toUpperCase()}
                    </div>
                    <div className={`text-sm font-semibold text-center ${done ? 'text-emerald-300 line-through opacity-70' : 'text-white'}`}>
                      {c.nombre}
                    </div>
                    {done && (
                      <div className="text-lg font-black text-emerald-400 tabular-nums mt-1" data-testid={`tiempo-${c.id}`}>
                        {fmtFull(c.tiempo_ms)}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </Card>
        )}

        {/* Finalizar */}
        {algunoConTiempo && (
          <div className="flex justify-center">
            <Button
              onClick={finalizar}
              disabled={finishing}
              className="bg-gold hover:bg-amber-500 text-black text-xl font-black px-8 py-7 rounded-2xl shadow-2xl shadow-gold/20"
              data-testid="btn-finalizar"
            >
              <Trophy className="h-6 w-6 mr-2" />
              {finishing ? 'Subiendo…' : 'Finalizar y subir al Hall of Fame'}
            </Button>
          </div>
        )}

        <p className="text-xs text-gray-500 text-center mt-6">
          Tip tablet: activa el sonido tocando el botón una vez · la pantalla se mantiene encendida en esta página.
        </p>
      </div>
    </div>
  );
}
